import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import messageThreadsApiService from '../../../services/messageThreadsApi';
import {
  getBackendUserId,
  getThreadAvatarInitial,
  getThreadHeaderSubtitle,
  getThreadHeaderTitle,
  getThreadListSubtitle,
  getThreadListTitle,
} from '../../../utils/messageThreadDisplay';
import { isMessageSentByViewer, normalizeIdentity } from '../../../utils/messageOwnership';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import { API_BASE_URL } from '../../../config/apiEndpoints';
import VenueMobileHeader from './VenueMobileHeader';
import {
  buildReadMapFromThreads,
  getThreadReadMap,
  hasThreadReadMap,
  isThreadUnreadForViewer,
  setThreadReadMap,
  syncThreadReadWithServer,
  threadListHasServerUnread,
} from '../../../utils/messageReadTracker';

const formatThreadTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  return isToday
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

const getUserId = (currentUser, userProfile) => getBackendUserId(currentUser, userProfile);

const getCounterpartyId = (messages, actorUserId) => {
  const actorId = normalizeIdentity(actorUserId);
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const senderId = normalizeIdentity(messages[i]?.sender_id);
    const receiverId = normalizeIdentity(messages[i]?.receiver_id);
    if (senderId && senderId !== actorId) return messages[i].sender_id;
    if (receiverId && receiverId !== actorId) return messages[i].receiver_id;
  }
  return null;
};

const MessagesSection = ({ venueId, initialThreadId = null, initialBookingId = null, onSectionChange }) => {
  const { currentUser, userProfile } = useAuth();
  const actorUserId = getUserId(currentUser, userProfile);
  const isAdmin = Boolean(userProfile?.is_admin || userProfile?.role === 'admin');
  const actorEmail = normalizeIdentity(currentUser?.email || userProfile?.email);
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [error, setError] = useState('');
  const [readMap, setReadMap] = useState({});
  const [venueOwnerId, setVenueOwnerId] = useState(null);
  const [activeThreadMeta, setActiveThreadMeta] = useState(null);
  const chatBodyRef = useRef(null);

  const activeThread = useMemo(
    () => threads.find((thread) => thread.id === activeThreadId) || null,
    [threads, activeThreadId]
  );

  useEffect(() => {
    if (!venueId) {
      setVenueOwnerId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const venue = await venueDashboardApiService.getVenueDetails(venueId);
        const ownerId = venue?.owner_id || venue?.owner?.id || null;
        if (!cancelled) setVenueOwnerId(ownerId);
      } catch {
        if (!cancelled) setVenueOwnerId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  useEffect(() => {
    const bookingId = activeThread?.booking_id || activeThread?.bookingId;
    if (!bookingId || activeThread?.guest_organizer_email || activeThread?.guestOrganizerEmail) {
      setActiveThreadMeta(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/bookings/${encodeURIComponent(String(bookingId))}`,
          { headers: venueDashboardApiService.getAuthHeaders() }
        );
        if (!response.ok) return;
        const booking = await response.json();
        const req = booking?.requirements || {};
        let parsedReq = req;
        if (typeof req === 'string') {
          try {
            parsedReq = JSON.parse(req);
          } catch {
            parsedReq = {};
          }
        }
        const guestEmail = parsedReq?.customer_info?.email || booking?.contact_email || null;
        if (!cancelled && guestEmail) {
          setActiveThreadMeta({
            guest_organizer_email: guestEmail,
            customer_id: booking?.customer_id || null,
            customer_email: booking?.customer_email || null
          });
        }
      } catch {
        if (!cancelled) setActiveThreadMeta(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeThread]);

  useEffect(() => {
    if (!actorUserId && !isAdmin) {
      setLoadingThreads(false);
      return;
    }
    if (!venueId && isAdmin) {
      setLoadingThreads(false);
      return;
    }
    const loadThreads = async () => {
      try {
        setLoadingThreads(true);
        setError('');
        // Venue dashboard: list by venue ownership (not participant user_id).
        const data = await messageThreadsApiService.listVenueDashboardThreads({
          userId: actorUserId,
          ownerId: actorUserId,
          venueId,
          limit: 50,
          isAdmin,
        });
        const filtered = messageThreadsApiService.filterThreadsForVenue(data, venueId);
        if (!hasThreadReadMap(actorUserId) && !threadListHasServerUnread(filtered)) {
          const initialReadMap = buildReadMapFromThreads(filtered);
          setThreadReadMap(actorUserId, initialReadMap);
          setReadMap(initialReadMap);
        } else {
          setReadMap(getThreadReadMap(actorUserId));
        }
        setThreads(filtered);
        const preferredById = initialThreadId
          ? filtered.find((thread) => String(thread.id) === String(initialThreadId))
          : null;
        const preferredByBooking = initialBookingId
          ? filtered.find((thread) => {
              const threadBookingId = thread?.booking_id || thread?.bookingId;
              return threadBookingId && String(threadBookingId) === String(initialBookingId);
            })
          : null;
        const preferredId = preferredById?.id || initialThreadId || preferredByBooking?.id || null;
        if (filtered.length > 0 || preferredId) {
          setActiveThreadId((prev) => preferredId || prev || filtered[0]?.id || null);
        } else {
          setActiveThreadId(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load threads');
      } finally {
        setLoadingThreads(false);
      }
    };
    loadThreads();
  }, [actorUserId, venueId, isAdmin, initialThreadId, initialBookingId]);

  useEffect(() => {
    if (!initialThreadId && !initialBookingId) return;
    const preferredById = initialThreadId
      ? threads.find((thread) => String(thread.id) === String(initialThreadId))
      : null;
    const preferredByBooking = initialBookingId
      ? threads.find((thread) => {
          const threadBookingId = thread?.booking_id || thread?.bookingId;
          return threadBookingId && String(threadBookingId) === String(initialBookingId);
        })
      : null;
    const preferredId = preferredById?.id || initialThreadId || preferredByBooking?.id;
    if (preferredId && preferredId !== activeThreadId) {
      setActiveThreadId(preferredId);
    }
  }, [initialThreadId, initialBookingId, threads, activeThreadId]);

  useEffect(() => {
    if (!activeThreadId || !actorUserId) return;
    const active = threads.find((thread) => thread.id === activeThreadId);
    if (!active) return;
    if (!isThreadUnreadForViewer(active, readMap)) return;
    syncThreadReadWithServer(actorUserId, activeThreadId, active.updated_at, {
      ownerId: actorUserId,
    }).then((next) => {
      setReadMap(next);
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === activeThreadId ? { ...thread, is_unread: false } : thread
        )
      );
    });
  }, [activeThreadId, actorUserId, threads, readMap]);

  const unreadThreadIds = useMemo(() => {
    const ids = new Set();
    threads.forEach((thread) => {
      if (isThreadUnreadForViewer(thread, readMap)) {
        ids.add(thread.id);
      }
    });
    return ids;
  }, [threads, readMap]);

  useEffect(() => {
    if (loadingMessages || !chatBodyRef.current) return;
    chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [messages, loadingMessages, activeThreadId]);

  useEffect(() => {
    if (!activeThreadId || !actorUserId) {
      setMessages([]);
      return;
    }
    const loadMessages = async () => {
      try {
        setLoadingMessages(true);
        setError('');
        const data = await messageThreadsApiService.getThreadMessagesForDashboard(activeThreadId, {
          userId: actorUserId,
          ownerId: actorUserId,
          isAdmin,
          limit: 100,
        });
        setMessages(data);
      } catch (err) {
        setError(err.message || 'Failed to load messages');
      } finally {
        setLoadingMessages(false);
      }
    };
    loadMessages();
  }, [activeThreadId, actorUserId, isAdmin]);

  const handleSend = async () => {
    if (!composeText.trim() || !activeThreadId || !actorUserId) return;
    const messageSenderId = venueOwnerId || actorUserId;
    const receiverId = getCounterpartyId(messages, messageSenderId);
    try {
      await messageThreadsApiService.sendMessageForDashboard(activeThreadId, composeText, {
        userId: actorUserId,
        ownerId: actorUserId,
        isAdmin,
        senderId: messageSenderId,
        ...(receiverId ? { receiverId } : {}),
      });
      setComposeText('');
      const data = await messageThreadsApiService.getThreadMessagesForDashboard(activeThreadId, {
        userId: actorUserId,
        ownerId: actorUserId,
        isAdmin,
        limit: 100,
      });
      setMessages(data);
      const active = threads.find((thread) => thread.id === activeThreadId);
      if (active) {
        const next = await syncThreadReadWithServer(actorUserId, activeThreadId, active.updated_at, {
          ownerId: actorUserId,
          existingMap: readMap,
        });
        setReadMap(next);
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === activeThreadId ? { ...thread, is_unread: false } : thread
          )
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to send message');
    }
  };

  const resolvedVenueOwnerId =
    activeThread?.venue_owner_id || activeThread?.venueOwnerId || venueOwnerId;

  const threadForOwnership = useMemo(() => {
    if (!activeThread) return null;
    if (activeThread.guest_organizer_email || activeThread.guestOrganizerEmail) return activeThread;
    if (activeThreadMeta) return { ...activeThread, ...activeThreadMeta };
    return activeThread;
  }, [activeThread, activeThreadMeta]);

  const messageSentByViewer = (message) =>
    isMessageSentByViewer(message, {
      actorUserId,
      actorEmail,
      viewerContext: 'venue',
      thread: threadForOwnership,
      venueOwnerId: resolvedVenueOwnerId
    });

  return (
    <div className="vd-messages">
      <VenueMobileHeader activeSection="comms" onSectionChange={onSectionChange} />
      <aside className="vd-messages-left">
        <h2 className="vd-messages-left-title">Messages</h2>
        <div className="vd-messages-thread-list">
          {loadingThreads && <p className="vd-messages-note">Loading threads...</p>}
          {!loadingThreads && threads.length === 0 && <p className="vd-messages-note">No message threads found.</p>}
          {threads.map((thread) => (
            <button
              type="button"
              key={thread.id}
              className={`vd-messages-thread ${thread.id === activeThreadId ? 'vd-messages-thread--active' : ''}`}
              onClick={() => setActiveThreadId(thread.id)}
            >
              <div className="vd-messages-avatar">{getThreadAvatarInitial(thread)}</div>
              <div className="vd-messages-thread-copy">
                <p className="vd-messages-thread-name">{getThreadListTitle(thread)}</p>
                <p className="vd-messages-thread-preview">{getThreadListSubtitle(thread)}</p>
              </div>
              <div className="vd-messages-thread-right">
                <span className="vd-messages-thread-time">{formatThreadTime(thread.updated_at)}</span>
                {unreadThreadIds.has(thread.id) ? <span className="vd-messages-unread-pill">1</span> : null}
              </div>
            </button>
          ))}
        </div>
      </aside>

      <main className="vd-messages-right">
        <header className="vd-messages-chat-header">
          <div className="vd-messages-chat-user">
            <div className="vd-messages-avatar vd-messages-avatar--lg">
              {activeThread ? getThreadAvatarInitial(activeThread) : 'M'}
            </div>
            <div className="vd-messages-chat-user-copy">
              <h3>{activeThread ? getThreadHeaderTitle(activeThread) : 'Select a thread'}</h3>
              {activeThread ? (
                <p className="vd-messages-chat-user-sub">{getThreadHeaderSubtitle(activeThread)}</p>
              ) : null}
            </div>
          </div>
          <button type="button" className="vd-messages-info-btn" aria-label="Conversation info">i</button>
        </header>

        <div className="vd-messages-chat-body" ref={chatBodyRef}>
          {loadingMessages && <p className="vd-messages-note">Loading messages...</p>}
          {!loadingMessages && messages.length === 0 && <p className="vd-messages-note">No messages yet.</p>}
          {messages.map((message) => {
            const sentMessage = messageSentByViewer(message);
            return (
              <div
                key={message.id}
                className={`vd-messages-row ${sentMessage ? 'vd-messages-row--me' : ''}`}
              >
                {!sentMessage && (
                <div className="vd-messages-avatar vd-messages-avatar--sm">
                  {(message.sender_name || 'U').charAt(0)}
                </div>
              )}
                <div className={`vd-messages-bubble ${sentMessage ? 'vd-messages-bubble--me' : ''}`}>
                  <p>{message.body}</p>
                  <span>{formatThreadTime(message.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>

        <footer className="vd-messages-input-wrap">
          <input
            type="text"
            placeholder="Type your message"
            value={composeText}
            onChange={(e) => setComposeText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            disabled={!activeThreadId}
          />
          <button type="button" aria-label="Send" onClick={handleSend} disabled={!activeThreadId || !composeText.trim()}>→</button>
        </footer>
        {error && <p className="vd-messages-note">{error}</p>}
      </main>
    </div>
  );
};

export default MessagesSection;
